/**
 * PLATFORM_ADMIN read-only org viewer, client half: the pure state (no
 * Firebase imports, so AuthContext and its tests stay free of them). The
 * request filing lives in platformAdmin.ts.
 *
 * The server half is functions/src/platformAdmin/platformAdminHandlers.ts;
 * the authority is in firestore.rules / storage.rules (isPlatformAdminViewing).
 * This file only reads the token claims into a view state and derives the
 * profile the rest of the app sees while a view is open. Nothing here can
 * grant access: a forged client state still meets the rules, which admit
 * reads only.
 */
import { type UserProfile } from '@/types/team';

// Mirrors functions/src/platformAdmin/platformAdminHandlers.ts and the rules
// (src/lib/__tests__/platformAdminParity.test.ts keeps those in step), and
// GamutServer pkg/services/portal/middleware/platform_admin_view.go, which no
// test here can reach: change all of them together.
export const PLATFORM_ADMIN_CLAIM = 'platformAdmin';
export const VIEW_ORG_CLAIM = 'paOrg';
export const VIEW_EXP_CLAIM = 'paExp';

export interface PlatformAdminState {
    isPlatformAdmin: boolean;
    /**
     * The session meets the rules' isPlatformAdminSession: Google sign-in
     * under a verified @aams.ai address. Without it the rules refuse every
     * view read and every list/start request, so no view is open here either.
     */
    qualifyingSession: boolean;
    /** Org being viewed, or null when no unexpired view is open. */
    viewOrgId: string | null;
    /** Epoch ms when the open view ends, or null. */
    viewExpiresAt: number | null;
}

export const NO_PLATFORM_ADMIN: PlatformAdminState = { isPlatformAdmin: false, qualifyingSession: false, viewOrgId: null, viewExpiresAt: null };

/** Mirrors firestore.rules / storage.rules isPlatformAdminSession. */
export function isQualifyingSession(claims: Record<string, unknown> | null | undefined): boolean {
    const firebase = claims?.firebase as { sign_in_provider?: unknown } | undefined;
    const email = typeof claims?.email === 'string' ? claims.email.toLowerCase() : '';
    return firebase?.sign_in_provider === 'google.com' && claims?.email_verified === true && /@aams\.ai$/.test(email);
}

export function platformAdminStateFromClaims(
    claims: Record<string, unknown> | null | undefined,
    now: number,
): PlatformAdminState {
    const isPlatformAdmin = claims?.[PLATFORM_ADMIN_CLAIM] === true;
    const qualifyingSession = isPlatformAdmin && isQualifyingSession(claims);
    const org = claims?.[VIEW_ORG_CLAIM];
    const exp = claims?.[VIEW_EXP_CLAIM];
    const viewing = qualifyingSession && typeof org === 'string' && org !== '' && typeof exp === 'number' && exp > now;
    return {
        isPlatformAdmin,
        qualifyingSession,
        viewOrgId: viewing ? (org as string) : null,
        viewExpiresAt: viewing ? (exp as number) : null,
    };
}

/**
 * The profile every page reads (useAuth().profile) while a view is open: the
 * viewed org, with org-wide (OWNER) scope so the queries cover the whole org,
 * no office or department of its own, and the platformAdminView marker that
 * useRBAC turns into read-only. The real profile stays on useAuth().realProfile.
 */
export function effectiveProfile(real: UserProfile | null, pa: PlatformAdminState): UserProfile | null {
    if (!real || !pa.viewOrgId || real.status === 'inactive') return real;
    return {
        ...real,
        orgId: pa.viewOrgId,
        role: 'OWNER',
        officeId: undefined,
        departmentId: undefined,
        platformAdminView: true,
    };
}

